import { action, observable, makeObservable } from 'mobx';
import moment from 'moment';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Enums from '../Enums';
import { isNotNullOrUndefined } from 'Util/TypeGuards';

export interface ITransactionEntityAttributes extends IModelAttributes {
	expirationTime: Date;
	status: Enums.transactionStatus;
	price: number;
	providerPaymentId: string;
	invoiceNumber: string;
	invoiced: boolean;
	initiallyUnpaid: boolean;
	terminalPayment: boolean;
	legacyId: string;
	humanReadableId: string;

	alterations: Array<Models.AlterationEntity | Models.IAlterationEntityAttributes>;
	discounts: Array<Models.TransactionDiscount | Models.ITransactionDiscountAttributes>;
	feess: Array<Models.FeeEntity | Models.IFeeEntityAttributes>;
	giftCertificateUsages: Array<Models.GiftCertificateUsageEntity | Models.IGiftCertificateUsageEntityAttributes>;
	giftCertificateId?: string;
	giftCertificate?: Models.GiftCertificateEntity | Models.IGiftCertificateEntityAttributes;
	/**
	 * True when transaction holds bookings with only one RESERVED alteration each. False otherwise.
	 */
	hasOnlyNewBookings?: boolean;
	/**
	 * True when transaction holds bookings that have been previously invoiced (when booking has an associated
	 * INVOICED or PENDING_INVOICE transaction). False otherwise.
	 */
	canInvoiceBookings?: boolean;
	secondsUntilExpiry?: number;
	refunds: Array<Models.RefundEntity | Models.IRefundEntityAttributes>;
	cardType?: string;
	inCheckIn?: boolean;
	userEntitlementId?: string;
	userEntitlement?: Models.UserEntitlementEntity | Models.IUserEntitlementEntityAttributes;
	purchasedItemId?: string;
	purchasedItem?: Models.PurchasedItemEntity | Models.IPurchasedItemEntityAttributes;
}
@entity('TransactionEntity', 'Transaction')
export default class TransactionEntity extends Model implements ITransactionEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<TransactionEntity, Date>()
	public expirationTime: Date;

	@observable
	@attribute<TransactionEntity, Enums.transactionStatus>()
	public status: Enums.transactionStatus;

	@observable
	@attribute<TransactionEntity, number>()
	public price: number;

	/**
	 * The code provided by eway
	 */
	@observable
	@attribute<TransactionEntity, string>()
	public providerPaymentId: string;

	@observable
	@attribute<TransactionEntity, string>()
	public invoiceNumber: string;

	/**
	 * True if payment is completed by third party service, false otherwise
	 */
	@observable
	@attribute<TransactionEntity, boolean>()
	public invoiced: boolean;

	@observable
	@attribute<TransactionEntity, boolean>()
	public initiallyUnpaid: boolean;

	@observable
	@attribute<TransactionEntity, boolean>()
	public terminalPayment: boolean;

	@observable
	@attribute<TransactionEntity, string>()
	public legacyId: string;

	@observable
	@attribute<TransactionEntity, string>()
	public cardType?: string;

	@observable
	@attribute<TransactionEntity, boolean>()
	public inCheckIn?: boolean;

	@observable
	@attribute()
	public userEntitlementId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public userEntitlement: Models.UserEntitlementEntity;

	@observable
	@attribute()
	public purchasedItemId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public purchasedItem: Models.PurchasedItemEntity;

	/**
	 * The ID that is displayed on the frontend
	 */
	@observable
	@attribute<TransactionEntity, string>()
	public humanReadableId: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public alterations: Models.AlterationEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public discounts: Models.TransactionDiscount[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public feess: Models.FeeEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public giftCertificateUsages: Models.GiftCertificateUsageEntity[] = [];

	@observable
	@attribute()
	public giftCertificateId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public giftCertificate: Models.GiftCertificateEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public refunds: Models.RefundEntity[] = [];

	public hasOnlyNewBookings?: boolean;

	public canInvoiceBookings?: boolean;

	public secondsUntilExpiry?: number;

	constructor(attributes?: Partial<ITransactionEntityAttributes>) {
		super(attributes);

		this.alterations = [];
		this.discounts = [];
		this.feess = [];
		this.giftCertificateUsages = [];
		this.refunds = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ITransactionEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.expirationTime !== undefined) {
				if (attributes.expirationTime === null) {
					this.expirationTime = attributes.expirationTime;
				} else {
					this.expirationTime = moment(attributes.expirationTime).toDate();
				}
			}
			if (attributes.status !== undefined) {
				this.status = attributes.status;
			}
			if (attributes.providerPaymentId !== undefined) {
				this.providerPaymentId = attributes.providerPaymentId;
			}
			if (attributes.invoiceNumber !== undefined) {
				this.invoiceNumber = attributes.invoiceNumber;
			}
			if (attributes.invoiced !== undefined) {
				this.invoiced = attributes.invoiced;
			}
			if (attributes.initiallyUnpaid !== undefined) {
				this.initiallyUnpaid = attributes.initiallyUnpaid;
			}
			if (attributes.terminalPayment !== undefined) {
				this.terminalPayment = attributes.terminalPayment;
			}
			if (attributes.legacyId !== undefined) {
				this.legacyId = attributes.legacyId;
			}
			if (attributes.humanReadableId !== undefined) {
				this.humanReadableId = attributes.humanReadableId;
			}
			if (attributes.alterations !== undefined && Array.isArray(attributes.alterations)) {
				if (!this.alterations) {
					this.alterations = [];
				}
				for (const model of attributes.alterations) {
					if (model instanceof Models.AlterationEntity) {
						this.alterations.push(model);
					} else {
						this.alterations.push(new Models.AlterationEntity(model));
					}
				}
			}
			if (attributes.discounts !== undefined && Array.isArray(attributes.discounts)) {
				if (!this.discounts) {
					this.discounts = [];
				}
				for (const model of attributes.discounts) {
					if (model instanceof Models.TransactionDiscount) {
						this.discounts.push(model);
					} else {
						this.discounts.push(new Models.TransactionDiscount(model));
					}
				}
			}
			if (attributes.feess !== undefined && Array.isArray(attributes.feess)) {
				if (!this.feess) {
					this.feess = [];
				}
				for (const model of attributes.feess) {
					if (model instanceof Models.FeeEntity) {
						this.feess.push(model);
					} else {
						this.feess.push(new Models.FeeEntity(model));
					}
				}
			}
			if (attributes.giftCertificateUsages !== undefined && Array.isArray(attributes.giftCertificateUsages)) {
				if (!this.giftCertificateUsages) {
					this.giftCertificateUsages = [];
				}
				for (const model of attributes.giftCertificateUsages) {
					if (model instanceof Models.GiftCertificateUsageEntity) {
						this.giftCertificateUsages.push(model);
					} else {
						this.giftCertificateUsages.push(new Models.GiftCertificateUsageEntity(model));
					}
				}
			}
			if (attributes.refunds !== undefined && Array.isArray(attributes.refunds)) {
				if (!this.refunds) {
					this.refunds = [];
				}
				for (const model of attributes.refunds) {
					if (model instanceof Models.RefundEntity) {
						this.refunds.push(model);
					} else {
						this.refunds.push(new Models.RefundEntity(model));
					}
				}
			}
			if (attributes.giftCertificateId !== undefined) {
				this.giftCertificateId = attributes.giftCertificateId;
			}
			if (attributes.giftCertificate !== undefined) {
				if (attributes.giftCertificate === null) {
					this.giftCertificate = attributes.giftCertificate;
				} else if (attributes.giftCertificate instanceof Models.GiftCertificateEntity) {
					this.giftCertificate = attributes.giftCertificate;
					this.giftCertificateId = attributes.giftCertificate.id;
				} else {
					this.giftCertificate = new Models.GiftCertificateEntity(attributes.giftCertificate);
					this.giftCertificateId = this.giftCertificate.id;
				}
			}
			if (isNotNullOrUndefined(attributes.canInvoiceBookings)) {
				this.canInvoiceBookings = attributes.canInvoiceBookings;
			}
			if (isNotNullOrUndefined(attributes.hasOnlyNewBookings)) {
				this.hasOnlyNewBookings = attributes.hasOnlyNewBookings;
			}
			if (isNotNullOrUndefined(attributes.secondsUntilExpiry)) {
				this.secondsUntilExpiry = attributes.secondsUntilExpiry;
			}
			if (isNotNullOrUndefined(attributes.cardType)) {
				this.cardType = attributes.cardType;
			}
			if (isNotNullOrUndefined(attributes.inCheckIn)) {
				this.inCheckIn = attributes.inCheckIn;
			}
			if (attributes.userEntitlementId !== undefined) {
				this.userEntitlementId = attributes.userEntitlementId;
			}
			if (attributes.userEntitlement !== undefined) {
				if (attributes.userEntitlement === null) {
					this.userEntitlement = attributes.userEntitlement;
				} else if (attributes.userEntitlement instanceof Models.UserEntitlementEntity) {
					this.userEntitlement = attributes.userEntitlement;
					this.userEntitlementId = attributes.userEntitlement.id;
				} else {
					this.userEntitlement = new Models.UserEntitlementEntity(attributes.userEntitlement);
					this.userEntitlementId = this.userEntitlement.id;
				}
			}
			if (attributes.purchasedItemId !== undefined) {
				this.purchasedItemId = attributes.purchasedItemId;
			}
			if (attributes.purchasedItem !== undefined) {
				if (attributes.purchasedItem === null) {
					this.purchasedItem = attributes.purchasedItem;
				} else if (attributes.purchasedItem instanceof Models.PurchasedItemEntity) {
					this.purchasedItem = attributes.purchasedItem;
					this.purchasedItemId = attributes.purchasedItem.id;
				} else {
					this.purchasedItem = new Models.PurchasedItemEntity(attributes.purchasedItem);
					this.purchasedItemId = this.purchasedItem.id;
				}
			}
		}
	}
}
